import { format } from "date-fns";
import { ArrowLeft, Calendar, Clock, FolderPlus } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Card } from "@/components/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { useGetPlaylist } from "@/features/playlists/api/get-playlist";
import { AddToPlaylist } from "@/features/playlists/components/add-to-playlist-modal";
import { CommentsContainer } from "@/features/recording-comments/components/comments-container";
import { AddTagForm } from "@/features/recording-tags/components/add-tag.form";
import { TagList } from "@/features/recording-tags/components/tag-list";
import { useGetRecording } from "@/features/recordings/api/get-recording";
import { RecordingChapters } from "@/features/recordings/components/recording-chapters";
import { RecordingPageActions } from "@/features/recordings/components/recording-page-actions";
import { RecordingPlayer } from "@/features/recordings/components/recording-player";
import { RecordingSkeleton } from "@/features/recordings/components/recording-skeleton";
import { RecordingSummary } from "@/features/recordings/components/recording-summary";
import { RecordingTitle } from "@/features/recordings/components/recording-title";
import { ViewTranscriptDrawer } from "@/features/recordings/components/view-transcript-drawer";
import { bytesToGi } from "@/features/recordings/utils/bytes-to-gb";
import { useRecordingViewTracker } from "@/features/recordings/utils/use-recording-view-tracker";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { queryClient } from "@/lib/react-query";
import {
  CanManageTags,
  PublicPlaylist,
  RoleEnum,
  RoleGroupEnum,
} from "@classview/shared-types";

import { ErrorMessage } from "../components/error-message";
import { UpgradeCard } from "../components/upgrade-card";

export function RecordingPage() {
  const { id } = useParams();
  const { organisation, hasPermission } = useOrganisation();
  const [searchParams] = useSearchParams();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const playlistId = searchParams.get("playlistId");
  const [isNameEditing, setIsNameEditing] = useState(false);

  const {
    data: recording,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetRecording(
    {
      organisationId: organisation.id,
      id: id || "",
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    data: playlist,

    isError: isPlaylistError,
    error: playlistError,
  } = useGetPlaylist(
    {
      organisationId: organisation.id,
      id: playlistId || "",
    },
    {
      enabled: !!playlistId,
      placeholderData: () => {
        const availablePlaylists = queryClient.getQueryData<PublicPlaylist[]>([
          "playlists",
          organisation.id,
        ]);
        if (!availablePlaylists) {
          return undefined;
        }

        const playlist = availablePlaylists.find((d) => d.id === playlistId);
        if (!playlist) {
          return undefined;
        }
        return {
          ...playlist,
          recordings: [],
        };
      },
    },
  );

  // Track recording views automatically
  useRecordingViewTracker({
    recordingId: recording?.id,
    isPlaying,
    currentTime,
  });

  if (isLoading) {
    return <RecordingSkeleton />;
  }

  if (isError || isPlaylistError || !isSuccess) {
    return <ErrorMessage error={error || playlistError} />;
  }

  const formattedDate = format(recording.createdAt, "PP");

  const isOwner = recording.organisation.id === organisation.id;
  const canSeeTranscript = hasPermission([
    RoleEnum.REPLAY,
    RoleEnum.REPLAY_ADVANCED,
  ]);

  const canAddToPlaylist = hasPermission([
    RoleEnum.REPLAY,
    RoleEnum.REPLAY_ADVANCED,
  ]);

  const canManageTags =
    hasPermission(CanManageTags) &&
    (organisation.roleGroup === RoleGroupEnum.ADMIN ||
      organisation.roleGroup === RoleGroupEnum.USER) &&
    isOwner;

  const canSeeTags = hasPermission(CanManageTags);
  const displayUpgradeCard =
    !hasPermission([RoleEnum.REPLAY_ADVANCED]) &&
    (organisation.roleGroup === RoleGroupEnum.ADMIN ||
      organisation.roleGroup === RoleGroupEnum.USER);

  return (
    <section>
      <Button variant="ghost" asChild className="mb-5 hidden">
        <Link to="/recordings">
          <ArrowLeft className="mr-2 size-4" aria-hidden="true" />
          Return to Replay
        </Link>
      </Button>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/recordings">Replay</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {playlistId && (
            <>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to={`/recordings/playlists/${playlistId}`}>
                    {playlist?.name}
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </>
          )}

          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{recording.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <div className="mb-5 rounded-[calc(var(--radius)+2px)] border border-border p-1">
        <RecordingPlayer
          recording={recording}
          transcript={canSeeTranscript ? recording.transcript : undefined}
          videoRef={videoRef}
          setIsPlaying={setIsPlaying}
          setCurrentTime={setCurrentTime}
        />
      </div>
      <div className="flex items-center justify-between">
        <RecordingTitle
          isEditing={isNameEditing}
          setIsEditing={setIsNameEditing}
          recording={recording}
        />

        <div className="flex items-center gap-2">
          {canAddToPlaylist && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <FolderPlus className="mr-2 size-4" aria-hidden={true} />
                  Add to playlist
                </Button>
              </DialogTrigger>
              <AddToPlaylist recordingId={recording.id} />
            </Dialog>
          )}
          <RecordingPageActions
            recording={recording}
            isEditing={isNameEditing}
            setIsEditing={setIsNameEditing}
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <dl className="flex h-9 flex-row items-center space-x-3 self-start">
          <div className="flex items-center">
            <dt className="sr-only">Duration</dt>
            <dd
              className="flex items-center text-nowrap text-sm text-muted"
              title={`${bytesToGi(recording.size)} GB`}
            >
              <Clock
                className="mr-1 size-3 text-card-foreground"
                aria-hidden={true}
              />
              {recording.duration}
            </dd>
          </div>
          <div className="flex items-center">
            <dt className="sr-only">Recording date</dt>
            <dd className="flex items-center text-nowrap text-sm text-muted">
              <Calendar
                className="mr-1 size-3 text-card-foreground"
                aria-hidden={true}
              />
              {formattedDate}
            </dd>
          </div>
        </dl>
        {canManageTags && canSeeTags && (
          <div className="flex-shrink-0 self-start">
            <AddTagForm recording={recording} />
          </div>
        )}
        {canSeeTags && <TagList recording={recording} />}
      </div>
      <div className="mt-5 flex flex-col justify-between gap-5 lg:flex-row">
        <div className="flex w-full flex-col gap-5 lg:w-2/3">
          {recording.transcript && canSeeTranscript && (
            <Card>
              <Card.Header
                title="Video Overview"
                className="flex flex-row items-center justify-between"
              >
                <ViewTranscriptDrawer
                  sessionName={recording.name}
                  transcript={recording.transcript}
                />
              </Card.Header>
              {recording.summary && (
                <Card.Content>
                  <RecordingSummary
                    recordingId={recording.id}
                    summary={recording.summary}
                  />
                </Card.Content>
              )}
            </Card>
          )}
          <CommentsContainer videoRef={videoRef} />
        </div>

        <RecordingChapters
          recordingId={recording.id}
          chapters={recording.chapters || []}
          videoRef={videoRef}
          currentTime={currentTime}
        />

        {displayUpgradeCard && (
          <div className="w-full self-start lg:w-1/3">
            <UpgradeCard
              title="Ready for Recordings Plus?"
              description="Unlock the full power of recordings with Recordings Plus. Time stamps, tagging, comments and more."
              buttonText="Find out More"
            />
          </div>
        )}
      </div>
    </section>
  );
}
